import React, { useState, useEffect } from 'react';
import { Link, useParams, useHistory } from "react-router-dom";
import { useGlobal } from 'reactn';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import SwiperCore, { Navigation, Thumbs } from 'swiper/core';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import timeGridPlugin from '@fullcalendar/timegrid';
import allLocales from '@fullcalendar/core/locales-all';

import { useApi } from 'stores/api';
import { fmtPrice, fmtPercent, imgUrl } from 'stores/utils';
import { ATTACHMENT_TYPE } from 'stores/const';
import { Focuspoint } from 'components/Focuspoint';
import Loader from 'components/Loader';
import ThumbsSlider from 'components/ThumbsSlider';
import FixedBottom from 'components/FixedBottom';

SwiperCore.use([Navigation, Thumbs]);

const ObjectDetails = (props) => {

  const { t } = useTranslation(['objectdetails']);
  let currentLang = i18n.languages[0];
  if (currentLang === 'en') currentLang = 'en-gb';
  console.log('the current language:' + currentLang);

  let { objectId } = useParams();
  let history = useHistory();
  const api = useApi();

  const [cartCount] = useGlobal('cartCount');
  const [searchParams] = useGlobal('searchParams');
  const [currentTxnId, setCurrentTxnId] = useGlobal('currentTxnId');
  const [masterData] = useGlobal('masterData');
  const [showLoader, setShowLoader] = useGlobal('showLoader');
  const [orderId] = useGlobal('orderId');
  const [objectDetails] = useGlobal('objectDetails');
  const [modalProps, setModalProps] = useGlobal('modalProps');
  const [images, setImages] = useState([]);
  const [docs, setDocs] = useState([]);
  const [properties, setProperties] = useState([]);

  const calendarRef = React.useRef();
  const [calendarView, setCalendarView] = useState('timeGridWeek');
  const [eventSourcesArr, setEventSourcesArr] = useState([]);
  const [inited, setInited] = useState(false);
  const [isBookableOnline, setIsBookableOnline] = useState(true);
  const [currentStartStr, setCurrentStartStr] = useState('');
  const [currentEndStr, setCurrentEndStr] = useState('');
  const [processing, setProcessing] = useState(false);

  const getImages = () => {
    let images = objectDetails.rentalObject.RentalObjectAttachment.filter(item => item.refRentalObjectAttachmentTypeId === ATTACHMENT_TYPE.IMAGE);
    setImages([...images]);
  }

  const getDocs = () => {
    let docs = objectDetails.rentalObject.RentalObjectAttachment.filter(item => item.refRentalObjectAttachmentTypeId !== ATTACHMENT_TYPE.IMAGE);
    setDocs([...docs]);
  }

  const getProperties = () => {
    if (!objectDetails.rentalObject.RentalObjectRentalParameterValue) return;
    let results = [];
    objectDetails.rentalObject.RentalObjectRentalParameterValue.forEach(pmt => {
      let typeName = pmt.DataType_Name;
      let vkey = `${typeName}Value`;
      let tempValue = pmt[vkey];
      let value;
      switch (typeName) {
        case 'Boolean':
          value = tempValue ? 'Ja' : 'Nein';
          break;
        case 'Enum':
          value = pmt[`${vkey}_Name`];
          break;
        default:
          value = tempValue;
      }
      results.push({
        Name: pmt.RentalParameter_Name,
        Value: value
      });
    });
    setProperties([...results]);
  }

  const imageSwiperProps = {
    direction: 'horizontal',
    slidesPerView: 'auto',
    grabCursor: false,
    spaceBetween: 2
  }

  const thumbSwiperProps = {
    spaceBetween: 0,
    touchRatio: 0.2,
    slideToClickedSlide: true,
    centeredSlides: true,
    direction: 'vertical',
    mousewheelControl: false,
    mousewheelForceToAxis: true,
    mousewheelSensitivity: 1,
    slidesPerView: 'auto',
    nextButton: '.swiper-button-next',
    prevButton: '.swiper-button-prev',
  }

  const sortDynamic = (key, order = 'asc') => {
    const sortOrder = order === 'asc' ? 1 : -1
    return (a, b) => {
      const A = (typeof a[key] === 'string') ? a[key].toUpperCase() : a[key];
      const B = (typeof b[key] === 'string') ? b[key].toUpperCase() : b[key];
      if (A < B) {
        return sortOrder * -1
      } else if (A > B) {
        return sortOrder * 1
      } else {
        return 0
      }
    }
  }


  const rentalObjectOffers = () => {
    const offers = objectDetails.rentalObject.RentalObjectOffer;
    const currency = objectDetails.businessUnit.Currency_Name;
    const getOfferPrice = (o) => {
      return o.FlatPrice || o.OfferPrice;
    }
    return (
      <div className="offers-entry">
        {
          offers.length === 0 &&
          <p>{t("noOfferItems")}</p>
        }
        {offers.sort(sortDynamic('Name')).map((offer, idx) =>
          <div className="list-entry" key={idx}>
            <div className="input">
              {offer.Name}
            </div>
            <div className="value">{currency} {fmtPrice(getOfferPrice(offer))}</div>
            <div className="price">{currency} {fmtPrice(getOfferPrice(offer))}</div>
          </div>
        )}
      </div>
    )
  }

  const hasConsumables = () => {
    return objectDetails.rentalObject.RentalObjectConsumable.length > 0;
  }

  const rentalObjectConsumables = () => {
    const consumables = objectDetails.rentalObject.RentalObjectConsumable;
    const currency = objectDetails.businessUnit.Currency_Name;
    const getOfferPrice = (o) => {
      return o.FlatPrice || o.OfferPrice;
    }
    return (
      <div className="consumables-entry">
        {
          consumables.length === 0 ?
            <p>{t("noConsumables")}</p> :
            <div className="consumables-info">{t("consumablesTabDescription")}</div>
        }
        {consumables.map((consumable, idx1) =>
          <div className="consumables-entry" key={idx1}>
            <div className="consumables-title">
              {consumable.Name}
            </div>
            {consumable.RentalObjectConsumableOffer.sort(sortDynamic('RentalObjectOffer_Name')).map((offer, idx2) =>
              <div className={"list-entry"} key={idx2}>
                <div className="input">{offer.RentalObjectOffer_Name}</div>
                <div className="value">{offer.FreeUnitQuantity} {consumable.Consumable_Unit}</div>
              </div>
            )}
            <div className="consumables-note">
              {consumable.MinQuantity > 0 && <div className="minimum">
                {t("consumableMinimum")} {consumable.MinQuantity} {consumable.Consumable_Unit}
              </div>}
              {currency} {fmtPrice(consumable.pricePerUnit)} {t("consumableFooterDescription")} {consumable.Consumable_Unit}
            </div>
          </div>
        )}
      </div>
    )
  }

  const hasOptions = () => {
    return objectDetails.objectOptions.Services.length > 0 || objectDetails.objectOptions.Accessories.length > 0
  }

  const rentalObjectOptions = () => {
    const services = objectDetails.objectOptions.Services;
    const objAccessories = objectDetails.objectOptions.Accessories || [];
    const accGrouped = Object.groupBy(objAccessories, ({ AccessoryRentalObject_RentalSubcategory_Name }) => AccessoryRentalObject_RentalSubcategory_Name);

    const currency = objectDetails.businessUnit.Currency_Name;
    const getServicePrice = (svc) => {
      return !svc.PercentagePrice ? `${currency} ${fmtPrice(svc.FixedPrice)}` : `${fmtPercent(svc.PercentagePrice)}%`;
    }
    const getAccOfferPrice = (offer) => {
      let offerPrice = offer.OfferPrice || offer.FlatPrice;
      return `${currency} ${fmtPrice(offerPrice)}`;
    }
    return (
      <>
        {services.length === 0 && objAccessories.length === 0 &&
          <div className="options-entry">
            <p>{t("noOptions")}</p>
          </div>
        }
        {services.length > 0 && <div className="options-entry">
          <h5>{t("services")}</h5>
          {services.map((service, idx) =>
            <div className="list-entry">
              <div className="name">{service.Name}</div>
              {(!masterData.organization.FreeOfCharge) && <div className="value">{getServicePrice(service)}</div>}
            </div>
          )}
        </div>}
        {
          Object.entries(accGrouped).map(([groupName, accessories]) => {
            return (
              <div className="options-entry">
                {<h5 key={groupName}>{groupName}</h5>}
                {accessories.map((accessory, idx1) =>
                  <>
                    {
                      accessory.AccessoryRentalObjectOffer.map((offer, idx2) =>
                        <div className="list-entry" key={idx2}>
                          <div className="name">{accessory.AccessoryRentalObject_Name} - {offer.DisplayName}</div>
                          {(!masterData.organization.FreeOfCharge) && <div className="value">{getAccOfferPrice(offer)}</div>}
                        </div>
                      )
                    }
                  </>
                )}
              </div>
            )
          })
        }
      </>
    );
  }

  const onClickBooking = async (e) => {
    e.preventDefault();
    setCurrentTxnId('');
    if (cartCount) {
      let exactMatch = masterData.organization.SearchExact;
      if (processing) return;
      setProcessing(true);
      let txnId = await api.createTxn(objectId, exactMatch);
      if (txnId) {
        let options = objectDetails.objectOptions;
        const services = options.Services;
        const accessories = options.Accessories;
        if (services.length === 0 && accessories.length === 0) {
          console.log("no options to choose for the given object");
          const result = await api.createOrUpdateOrder(orderId, txnId);
          if (result) {
            history.push('/wizard/overview');
          }
        } else {
          console.log("options available to choose for the given object");
          history.push('/wizard/options/add');
        }
      }
    } else {
      history.push(`/wizard/book/${objectId}`);
    }
  }

  const displayProperties = () => {
    if (!properties.length) return null;
    return (
      <div className="product-properties">
        <div className="title">
          {t("properties")}
        </div>
        <ul className="properties-list">
          {properties.map((property) =>
            <li>
              <div className="properties-list-item name">{property.Name}</div>
              <div className="properties-list-item value">{property.Value ? property.Value : null}</div>
            </li>
          )}
        </ul>
      </div>
    )
  }

  const displayAttachments = () => {
    if (!docs.length) return null;
    return (
      <div className="product-attachments">
        <div className="title">
          {t("attachments")}
        </div>
        <ul className="attachments-list">
          {docs.map((attachment) =>
            <li>
              <a href={`/api/public/RentalObject/Attachment/${attachment.Id}`} target="_blank">
                <i className="fa fa-file-pdf-o"></i> <span>{attachment.Name || t("unnamedAttachment")}</span>
              </a>
            </li>
          )}
        </ul>
      </div>
    );
  }

  const showCancelFixCol = (cFees) => {
    return cFees.find(fee => fee.Fix > 0.0);
  }

  const cancellation = () => {
    const sortFn = (a, b) => (a.MinHoursPriorToPickup > b.MinHoursPriorToPickup) ? 1 : -1;
    const cancellationFees = objectDetails.businessUnit.BusinessUnitCancellationFee.sort(sortFn);
    const currency = objectDetails.businessUnit.Currency_Name;
    const itemsLen = cancellationFees.length;
    const maxHours = itemsLen ? cancellationFees[itemsLen - 1]['MinHoursPriorToPickup'] : 0;
    const cancelTimeunit = parseInt(maxHours, 10) >= 48 ? 'DAYS' : 'HOURS';
    return (
      <div className="cancelfee-entry">
        {cancellationFees.length === 0 ?
          <p>{t("noCancellationFees")}</p>
          :
          <div className="list-entry header">
            <div className="day">{cancelTimeunit == 'DAYS' ? t("daysToPickup") : t("hoursToPickup")}</div>
            {showCancelFixCol(cancellationFees) && <div className="fix">{t("fix")}</div>}
            <div className="percent">{t("percent")}</div>
          </div>
        }
        {cancellationFees.map((cancellationFee, idx) =>
          <div className={"list-entry"}>
            {cancelTimeunit == 'DAYS' && <div className="day">{cancellationFee.MinHoursPriorToPickup / 24}</div>}
            {cancelTimeunit == 'HOURS' && <div className="day">{cancellationFee.MinHoursPriorToPickup}</div>}
            {showCancelFixCol(cancellationFees) && <div className="fix">{currency} {fmtPrice(cancellationFee.Fix)}</div>}
            <div className="percent">{cancellationFee.Rate}%</div>
          </div>
        )}
      </div>
    );
  }

  const goNext = () => {
    let calendarApi = calendarRef.current.getApi();
    let view = calendarApi.view;
    let { type, title, currentStart, currentEnd, activeStart, activeEnd } = view;
    console.log(type);
    console.log(title);
    console.log(currentStart);
    console.log(currentEnd);
    console.log(activeStart);
    console.log(activeEnd);
    calendarApi.next();
  }

  const goPrev = () => {
    let calendarApi = calendarRef.current.getApi();
    calendarApi.previous();
  }

  const goSelection = (sDate) => {
    let { startDate } = searchParams;
    if (!startDate) startDate = sDate;
    if (startDate) {
      let calendarApi = calendarRef.current.getApi();
      calendarApi.gotoDate(startDate);
    }
  }

  const switchView = () => {
    let calendarApi = calendarRef.current.getApi();
    //calendarApi.changeView('timeGridDay');
    if (calendarView === 'timeGridWeek') {
      setCalendarView('dayGridMonth');
      calendarApi.changeView('dayGridMonth');
    } else {
      setCalendarView('timeGridWeek');
      calendarApi.changeView('timeGridWeek');
    }
  }

  const onDatesSet = (dateInfo) => {
    let { start, end, startStr, endStr, timeZone, view } = dateInfo;
    let { type, title } = view;
    console.log('---dates set----');
    console.log(view.type);
    console.log(type);
    console.log(title);
    console.log(start);
    console.log(end);
    console.log(startStr);
    console.log(endStr);
    console.log(timeZone);
    console.log('loaded initial');
    startStr = startStr.substr(0, 19);
    endStr = endStr.substr(0, 19);

    if (!(currentStartStr === startStr && currentEndStr === endStr)) {
      fetchNewEvents(startStr, endStr, type);
      setCurrentStartStr(startStr);
      setCurrentEndStr(endStr);
    }

    return [];
  }

  const fetchNewEvents = async (startStr, endStr, viewType) => {
    let { startDate, endDate } = searchParams;

    const contentUPDT = await getContentUPDT(startStr, endStr);
    const updtEvents = await getUPDTEvents(contentUPDT);
    const alreadyBookedEvents = await getAlreadyBookedEvents(startStr, endStr);
    const currentEvent = [{ start: startDate, end: endDate, title: t("selection") }];

    if (viewType === 'dayGridMonth') {
      setEventSourcesArr([
        {
          events: alreadyBookedEvents,
          className: 'calendar-booked',
        },
      ]);
    }
    else {
      setEventSourcesArr([
        {
          events: currentEvent,
          className: 'calendar-selection',
        },
        {
          events: updtEvents,
          className: 'calendar-updt',
        },
        {
          events: alreadyBookedEvents,
          className: 'calendar-booked',
        },
      ]);
      console.log('go selection...');
      if (!inited) {
        console.log('go selection2...');
        goSelection();
        setInited(true);
      }
    }
  }

  const getContentUPDT = async (startStr, endStr) => {
    return await api.getUnavailablePickupAndDropoffTimes(objectId, startStr, endStr);
  }

  const getUPDTEvents = async (contentUPDT) => {
    let updtItems = [];
    for (let outer of contentUPDT) {
      for (let inner of outer.UnavailablePickupAndDropoffInterval) {
        updtItems.push({
          start: (inner.DateFrom.substring(0, 19)),
          end: (inner.DateTo.substring(0, 19)),
        })
      }
    }
    return updtItems.map(item => {
      return {
        start: item.start,
        end: item.end,
        allDay: item.allDay,
      }
    });
  }

  const getAlreadyBookedEvents = async (startStr, endStr) => {
    let bookedEvents = [];
    let contentTAS = await api.getTimelineAllotmentWithSeasons(objectId, startStr, endStr);
    for (let outer of contentTAS) {
      if (!outer.IsBookable) {
        bookedEvents.push({
          start: (outer.TimeSlot.substring(0, 19)),
          end: (outer.TimeSlotEnd.substring(0, 19)),
        })
      }
    }
    return bookedEvents;
  }

  useEffect(() => {
    const fetchObjectDetails = async () => {
      let { startDate, endDate } = searchParams;
      if (!startDate || !endDate) {
        let newParams = {};
        await api.setDefaultDateTimes(newParams);
        startDate = newParams.startDate;
        endDate = newParams.endDate;
      }
      await api.fetchObjectDetails({ objectId, startDate, endDate, onlyExactMatch: cartCount > 0 });

      let calStart = new Date();
      let calEnd = new Date();
      calStart.setDate(startDate.getDate() - 28);
      calEnd.setDate(startDate.getDate() + 28);
      goSelection(searchParams.startDate ?? startDate);
      setShowLoader(false);
    };
    setShowLoader(true);
    fetchObjectDetails();
  }, [objectId]);

  useEffect(() => {
    if (objectDetails) {
      if (objectDetails.rentalObject.NotBookableOnline) {
        setIsBookableOnline(false);
      } else {
        setIsBookableOnline(true);
      }
      getImages();
      getDocs();
      getProperties();
    }
  }, [objectDetails])

  return (
    <>
      {showLoader && <Loader />}
      {
        objectDetails &&
        <>
          <section className="topdetails-container details-view">
            <div className="wrapper">
              <div className="row productDetail-container" data-scroll-index='0'>
                <div className="column-5">
                  <Link to="/search" className="back-link small"><i className="fa fa-arrow-left" aria-hidden="true"></i> {t("back")}</Link>
                  <div className="product-details">
                    <div className="product-name biggest">
                      {objectDetails.rentalObject.Name}
                    </div>
                    {(!masterData.organization.FreeOfCharge) && objectDetails.rentalObject.ResultPriceFrom > 0 && <div className="product-price">
                      <span className="from">{t("fromPrice")}</span>
                      <span className="amount">{objectDetails.businessUnit.Currency_Name} {fmtPrice(objectDetails.rentalObject.ResultPriceFrom)}</span>
                      <span className="duration">/ {objectDetails.rentalObject.PriceFromAllotmentTimeUnit_Name}</span>
                    </div>}
                    <div className="product-location">
                      <i className="fa fa-map-marker" aria-hidden="true"></i> <span>{objectDetails.businessUnit.Name}</span>
                    </div>
                  </div>
                  {!isBookableOnline && <div className="not-bookable-text">{t("notBookableOnline")}</div>}
                </div>
                <div className={"column-7 " + (images.length > 1 ? 'with-thumbs' : 'no-thumbs')}>
                  <ThumbsSlider images={images} />
                </div>
              </div>
            </div>
          </section >

          <section className="description-container calendar-container details-view white" data-scroll-index='1'>
            <div className="wrapper">
              <div className="row">

                <div className="column-5">
                  <div className="product-description">
                    <p className="product-claim">
                      {objectDetails.rentalObject.ShortDescription}
                    </p>
                    <p dangerouslySetInnerHTML={{ __html: objectDetails.rentalObject.Description }}>
                    </p>
                  </div>
                  {displayProperties()}
                  {displayAttachments()}
                </div>

                <div className="column-7">
                  <div className="explain-text">
                    <p className="title">{t("availableDates")}</p>
                    <p>{t("availableDatesDescription")}</p>
                  </div>
                  <div className="product-calendar detail">
                    <div className="calendar-main">
                      <FullCalendar

                        customButtons={{
                          yourSelection: {
                            text: t("yourSelection"),
                            click: goSelection
                          },
                        }}

                        ref={calendarRef}
                        plugins={[dayGridPlugin, timeGridPlugin]}
                        aspectRatio={1.75}
                        locales={allLocales}
                        locale={currentLang}
                        initialView={calendarView}
                        editable={false}
                        selectable={false}
                        allDaySlot={false}
                        displayEventTime={false}
                        weekends={true}
                        nowIndicator={true}
                        datesSet={onDatesSet}
                        eventSources={eventSourcesArr}
                        firstDay={1}
                        // initialEvents={[
                        //   { title: 'event 1', date: '2021-07-07' },
                        //   { title: 'event 2', date: '2021-07-11' }
                        // ]}
                        headerToolbar={{
                          left: 'yourSelection',
                          center: 'title',
                          right: 'prev,dayGridMonth,timeGridWeek,next',
                        }}
                        slotDuration={'00:60:00'}
                        slotEventOverlap={false}
                      // alternatively, use the `events` setting to fetch from a feed
                      /* select={this.handleDateSelect}
                       eventContent={renderEventContent} // custom render function
                       eventClick={this.handleEventClick}
                       eventsSet={this.handleEvents} // called after events are initialized/added/changed/removed
                        you can update a remote database when these fire:
                       eventAdd={function(){}}
                       eventChange={function(){}}
                       eventRemove={function(){}}
                       */
                      />

                    </div>
                    <div className="calendar-legend">
                      <div className="label">{t("legend")}</div>
                      <div className="legend-row actual-booking">
                        <div className="legend-color"></div>
                        <div className="legend-label">{t("lgdYourBooking")}</div>
                      </div>
                      <div className="legend-row booked">
                        <div className="legend-color"></div>
                        <div className="legend-label">{t("lgdNotAvailable")}</div>
                      </div>
                      <div className="legend-row pickup-drop">
                        <div className="legend-color"></div>
                        <div className="legend-label">{t("lgdStartEndTime")}</div>
                      </div>
                    </div>

                  </div>
                </div>

              </div>
            </div>
          </section>

          <section className="offers-container details-view">
            {(!masterData.organization.FreeOfCharge) && <div className="offersContainer-wrapper">
              <div className="wrapper">
                <div className="row">
                  <div className="column-8 offset-2">
                    <h4>{t("Offers")}</h4>
                    <div className="details-content offers" >
                      {rentalObjectOffers()}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
            {(!masterData.organization.FreeOfCharge) && hasConsumables() && <div className="offersContainer-wrapper">
              <div className="wrapper">
                <div className="row">
                  <div className="column-8 offset-2">
                    <h4>{t("Consumables")}</h4>
                    <div className="details-content consumables">
                      {rentalObjectConsumables()}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
            {objectDetails.objectOptions && hasOptions() && <div className="offersContainer-wrapper">
              <div className="wrapper">
                <div className="row">
                  <div className="column-8 offset-2">
                    <h4>{t("Options")}</h4>
                    <div className="details-content options" >
                      {rentalObjectOptions()}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
            {(!masterData.organization.FreeOfCharge) && <div className="offersContainer-wrapper">
              <div className="wrapper">
                <div className="row">
                  <div className="column-8 offset-2">
                    <h4>{t("Cancellation")}</h4>
                    <div className="details-content cancellation" >
                      {cancellation()}
                    </div>
                  </div>
                </div>
              </div>
            </div>}
          </section>

          <FixedBottom>
            <div className="wrapper">
              <div className="row fixedBottom-content">
                <div className="column-5 fixedBottom-right">
                  {isBookableOnline && <div className="fixedBottom-buttons btn-forward">
                    <Link to={`/wizard/book/${objectDetails.rentalObject.Id}`} onClick={onClickBooking} id="btn-book-object" className="book-object">
                      {t("bookObject")} <i className="fa fa-shopping-cart" aria-hidden="true"></i>
                    </Link>
                  </div>}
                </div>
                <div className="column-7 fixedBottom-left">
                  <div className="product-details">
                    <div className="product-name biggest">
                      {objectDetails.rentalObject.name}
                    </div>
                    {(!masterData.organization.FreeOfCharge) && (objectDetails.rentalObject.ResultPriceFrom > 0) && <div className="product-price">
                      <span className="from">{t("fromPrice")}</span>
                      <span className="amount">{objectDetails.businessUnit.Currency_Name} {fmtPrice(objectDetails.rentalObject.ResultPriceFrom)}</span>
                      <span className="duration">/ {objectDetails.rentalObject.PriceFromAllotmentTimeUnit_Name}</span>
                    </div>}
                  </div>
                </div>
              </div>
            </div>
          </FixedBottom>
        </>
      }
    </>
  );
}

export default ObjectDetails;